import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronUp, ChevronDown, Check, X, Clock, Shield, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PropertySource {
  id: string;
  source_type: string;
  source_value: string;
  api_endpoint?: string;
  confidence_score: number;
  last_updated: string;
  metadata: any;
}

interface PriorityRule {
  id: string;
  source_type: string;
  priority_order: number;
  is_active: boolean;
}

interface PropertySourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyName: string;
  tableName: string;
  recordId?: string;
}

export function PropertySourceModal({ isOpen, onClose, propertyName, tableName, recordId }: PropertySourceModalProps) {
  const [sources, setSources] = useState<PropertySource[]>([]);
  const [priorityRules, setPriorityRules] = useState<PriorityRule[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && propertyName) {
      fetchPropertySources();
      fetchPriorityRules();
    }
  }, [isOpen, propertyName, tableName, recordId]);

  const fetchPropertySources = async () => {
    if (!recordId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('property_sources')
        .select('*')
        .eq('table_name', tableName)
        .eq('record_id', recordId)
        .eq('property_name', propertyName)
        .order('last_updated', { ascending: false });

      if (error) throw error;
      setSources(data || []);
    } catch (error) {
      console.error('Error fetching property sources:', error);
      toast({
        title: "Error",
        description: "Failed to fetch property sources",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPriorityRules = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('property_priority_rules')
        .select('*')
        .eq('property_name', propertyName)
        .eq('is_active', true)
        .order('priority_order', { ascending: true });

      if (error) throw error;
      setPriorityRules(data || []);
    } catch (error) {
      console.error('Error fetching priority rules:', error);
      toast({
        title: "Error",
        description: "Failed to fetch priority rules",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updatePriorityOrder = async (ruleId: string, newOrder: number) => {
    try {
      const { error } = await supabase
        .from('property_priority_rules')
        .update({ priority_order: newOrder })
        .eq('id', ruleId);

      if (error) throw error;
      
      // Update local state
      setPriorityRules(prev => prev.map(rule => 
        rule.id === ruleId ? { ...rule, priority_order: newOrder } : rule
      ).sort((a, b) => a.priority_order - b.priority_order));

      toast({
        title: "Success",
        description: "Priority order updated successfully"
      });
    } catch (error) {
      console.error('Error updating priority:', error);
      toast({
        title: "Error",
        description: "Failed to update priority order",
        variant: "destructive"
      });
    }
  };

  const getSourceTypeIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'linkedin': return '💼';
      case 'storeleads': return '🏪';
      case 'b2b_enrichment': return '🔍';
      case 'hubspot': return '🧡';
      case 'manual': return '✋';
      case 'import': return '📤';
      default: return '📊';
    }
  };

  const getSourceTypeColor = (sourceType: string) => {
    switch (sourceType) {
      case 'linkedin': return 'bg-blue-100 text-blue-800';
      case 'storeleads': return 'bg-green-100 text-green-800';
      case 'b2b_enrichment': return 'bg-purple-100 text-purple-800';
      case 'hubspot': return 'bg-orange-100 text-orange-800';
      case 'manual': return 'bg-gray-100 text-gray-800';
      case 'import': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-neutral-100 text-neutral-800';
    }
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatLastUpdated = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return date.toLocaleDateString();
  };

  const moveRule = (ruleId: string, direction: 'up' | 'down') => {
    const currentIndex = priorityRules.findIndex(rule => rule.id === ruleId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= priorityRules.length) return;

    const currentRule = priorityRules[currentIndex];
    const swapRule = priorityRules[newIndex];

    updatePriorityOrder(currentRule.id, swapRule.priority_order);
    updatePriorityOrder(swapRule.id, currentRule.priority_order);
  };

  // Get the master value based on priority rules
  const getMasterValue = () => {
    for (const rule of priorityRules) {
      const source = sources.find(s => s.source_type === rule.source_type);
      if (source && source.source_value) {
        return { source, rule };
      }
    }
    return null;
  };

  const masterValue = getMasterValue();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Property Sources: {propertyName}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Priority Rules Section */}
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Priority Order</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Drag to reorder sources by priority. Higher priority sources override lower ones.
              </p>
            </div>

            <div className="space-y-2">
              {priorityRules.map((rule, index) => {
                const hasData = sources.some(s => s.source_type === rule.source_type);
                const isMaster = masterValue?.rule.id === rule.id;
                
                return (
                  <Card key={rule.id} className={`${isMaster ? 'ring-2 ring-primary' : ''}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveRule(rule.id, 'up')}
                              disabled={index === 0}
                              className="h-6 w-6 p-0"
                            >
                              <ChevronUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveRule(rule.id, 'down')}
                              disabled={index === priorityRules.length - 1}
                              className="h-6 w-6 p-0"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getSourceTypeIcon(rule.source_type)}</span>
                            <Badge className={getSourceTypeColor(rule.source_type)}>
                              {rule.source_type}
                            </Badge>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {isMaster && <Badge variant="default" className="text-xs">Master</Badge>}
                          {hasData ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <X className="h-4 w-4 text-red-400" />
                          )}
                          <span className="text-xs text-muted-foreground">
                            #{rule.priority_order}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Property Sources Section */}
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Available Data Sources</h3>
              <p className="text-sm text-muted-foreground mb-4">
                All data sources for this property, with confidence scores and metadata.
              </p>
            </div>

            {loading ? (
              <div className="text-center py-8">Loading sources...</div>
            ) : sources.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No data sources found for this property
              </div>
            ) : (
              <div className="space-y-3">
                {sources.map((source) => (
                  <Card key={source.id} className="transition-all hover:shadow-sm">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getSourceTypeIcon(source.source_type)}</span>
                          <Badge className={getSourceTypeColor(source.source_type)}>
                            {source.source_type}
                          </Badge>
                          {masterValue?.source.id === source.id && (
                            <Badge variant="default" className="text-xs">Current Master</Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className={`font-medium ${getConfidenceColor(source.confidence_score)}`}>
                            {source.confidence_score}%
                          </span>
                          <Clock className="h-3 w-3" />
                          <span>{formatLastUpdated(source.last_updated)}</span>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Value</Label>
                          <div className="font-mono text-sm bg-muted p-2 rounded">
                            {source.source_value}
                          </div>
                        </div>
                        
                        {source.api_endpoint && (
                          <div>
                            <Label className="text-xs text-muted-foreground">API Endpoint</Label>
                            <div className="flex items-center gap-1 text-xs">
                              <ExternalLink className="h-3 w-3" />
                              <code>{source.api_endpoint}</code>
                            </div>
                          </div>
                        )}
                        
                        {source.metadata && Object.keys(source.metadata).length > 0 && (
                          <div>
                            <Label className="text-xs text-muted-foreground">Metadata</Label>
                            <details className="text-xs">
                              <summary className="cursor-pointer text-primary">View metadata</summary>
                              <pre className="mt-1 bg-muted p-2 rounded text-xs overflow-x-auto">
                                {JSON.stringify(source.metadata, null, 2)}
                              </pre>
                            </details>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}